import * as Yup from 'yup';
import type { HTMLAttributes } from 'react';

import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import { EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { MemoConfirmDialog } from './MemoConfirmDialog';
import { DIALOG_AUTO_FOCUS_ATTRIBUTE } from '../../constants/dialog-auto-focus.constant';
import type { SubmitHandler } from './types/submit-handler.type';

export interface MoveToGroupValues {
  readonly targetServiceUsagesGroupId: Id;
}

export interface GroupOption {
  readonly _id: Id;
  readonly isOptional?: boolean;
  readonly label?: string | null;
}

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly isDialogOpen: boolean;
  readonly onCloseDialog: () => void;
  readonly onSubmit: SubmitHandler<MoveToGroupValues>;
  readonly targetGroups: readonly GroupOption[];
}

const validationSchema = Yup.object().shape({
  targetServiceUsagesGroupId: Yup.string().required('Groupe requis'),
});

export const MoveToGroupForm = ({
  askMemoBeforeSubmit,
  isDialogOpen,
  onCloseDialog,
  onSubmit,
  targetGroups,
}: Readonly<Props>) => (
  <MemoConfirmDialog<MoveToGroupValues>
    askMemoBeforeSubmit={askMemoBeforeSubmit}
    description="La prestation sera placée à la fin du groupe choisi."
    id="move-service-usage-to-group"
    initialValues={{
      targetServiceUsagesGroupId: (targetGroups[0]?._id ?? EMPTY_STRING) as Id,
    }}
    isDialogOpen={isDialogOpen}
    onCloseDialog={onCloseDialog}
    onSubmit={onSubmit}
    submitLabel="Déplacer"
    title="Déplacer la prestation"
    validationSchema={validationSchema}
  >
    {({ handleChange, values }) => (
      <TextField
        fullWidth
        id="move-service-usage-to-group-target"
        label="Groupe de destination"
        margin="normal"
        name="targetServiceUsagesGroupId"
        onChange={handleChange}
        required
        select
        slotProps={{
          select: {
            SelectDisplayProps: {
              [DIALOG_AUTO_FOCUS_ATTRIBUTE]: true,
            } as HTMLAttributes<HTMLDivElement>,
          },
        }}
        value={values.targetServiceUsagesGroupId.toString()}
      >
        {targetGroups.map((targetGroup) => (
          <MenuItem
            key={targetGroup._id.toString()}
            value={targetGroup._id.toString()}
          >
            {targetGroup.label}
          </MenuItem>
        ))}
      </TextField>
    )}
  </MemoConfirmDialog>
);
